import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RouterState } from "redux-first-history";
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWrench } from '@fortawesome/free-solid-svg-icons'

import { MachineActions } from "redux/actions/settings/MachineActions";
import { MachineHistoryActions } from "redux/actions/machines/MachineHistoryActions";
import { FuelTypeActions } from "redux/actions/settings/FuelTypeActions";
import { ModalActions } from "redux/actions/ModalActions";
import { TableColumn, Table, TableRow, sortType } from "app/components/common/Table";

import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { Machine } from "models/Machine";
import { MachineHistory } from "models/MachineHistory";
import { FuelType } from "models/FuelType";
import { MachineDefect } from "models/MachineDefect";
import { Unit } from "models/Unit";
import { CurrentSubscription } from "models/SubscriptionPlan";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import { Translations } from "models/Translations";
import Translator from "app/components/common/Translator";
import { Translate } from "infrastructure/mapping/Translate";


interface Props {
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fetchFuelTypes: typeof FuelTypeActions.fetchFuelTypes;
    deleteMachineHistories: typeof MachineHistoryActions.deleteMachineHistory;
    moveToEditDailyReport: typeof MachineHistoryActions.moveToEditDailyReport;
    moveToEditWorkshop: typeof MachineHistoryActions.moveToEditWorkshop;
    moveToAddDailyReport: typeof MachineHistoryActions.moveToAddDailyReport;
    editForm: typeof ModalActions.showModal;
    loginUser: LoginUser;
    machines: Machine[];
    machineHistories: MachineHistory[];
    fuelTypes: FuelType[];
    currentSubscription: CurrentSubscription | null;
    translations: Translations;
    router: RouterState;
}

interface State {
    selectedOption?: SelectedOption | null; // undefined until the user picks, null shows all machines
}

type SelectedOption = {
    label: string,
    value: number
}

class MachineHistories extends React.Component<Props, State> {
    public readonly state: State = {}

    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
            if (!this.props.fuelTypes) {
                this.props.fetchFuelTypes(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machines && this.props.machineHistories
    }

    getOptions(): SelectedOption[] {
        return this.props.machines
            .filter(m => !m.archivedDate)
            .map(m => ({ label: m.name, value: m.id }));
    }

    selectedOption() {
        if (this.state.selectedOption !== undefined)
            return this.state.selectedOption;
        const assigned = Machine.assignedTo(this.props.machines, this.props.loginUser.userData?.id);
        return this.getOptions().find(o => o.value === assigned?.id) ?? null;
    }

    defectsEnabled() {
        return !!this.props.currentSubscription?.subscriptionPlan.defectsEnabled;
    }

    evalMachineDefects(mh: MachineHistory) {
        if (mh.machineDefects && mh.machineDefects.length > 0)
            return mh.machineDefects[0] ? <>{mh.machineDefects[0].defect}</> : <></>;
        if (mh.fixedMachineDefects && mh.fixedMachineDefects.length > 0)
            return <>
                <FontAwesomeIcon icon={faWrench} className="c-table__cell--icon c-table__cell--icon-success"></FontAwesomeIcon>
                {mh.fixedMachineDefects[0].defect}
            </>;
        return <></>
    }

    evalMachineDefectVals(mh: MachineHistory) {
        if (mh.machineDefects && mh.machineDefects.length > 0) {
            return mh.machineDefects;
        }
        if (mh.fixedMachineDefects && mh.fixedMachineDefects.length > 0) {
            return mh.fixedMachineDefects
        }
        return [MachineDefect.empty()]
    }

    withUnit(value: number | string, unit?: Unit | null) {
        return unit && unit.short ? value + " " + unit.short : value + "";
    }

    evalFuelStop(mh: MachineHistory) {
        if (!mh.fuelStop)
            return '';
        // the fuel type list carries the unit, the one on the history entry might not
        const fuelUnit = mh.fuelStop.fuelType.unit ?? this.props.fuelTypes?.find(ft => ft.id === mh.fuelStop!.fuelType.id)?.unit;
        return this.withUnit(mh.fuelStop.quantity, fuelUnit) + " " + mh.fuelStop.fuelType.name;
    }

    evalMachineMaintenance(mh: MachineHistory) {
        if (mh.machineMaintenances)
            return mh.machineMaintenances[0] ? <>{mh.machineMaintenances[0].maintenance.name}</> : <></>
        return <></>
    }

    setMachineData(): TableRow[] {
        const machineId = this.selectedOption()?.value;
        let data: TableRow[] = [];
        {
            this.props.machineHistories.filter(mh => !machineId || mh.machine.id === machineId).forEach(machineHistory => {
                data.push({
                    dataObject: {
                        id: machineHistory.id,
                        machine: machineHistory.machine,
                        date: machineHistory.date,
                        serviceLife: machineHistory.serviceLife,
                        fuelStop: this.evalFuelStop(machineHistory),
                        ...(this.defectsEnabled() && { machineDefect: this.evalMachineDefectVals(machineHistory) }),
                        machineMaintenance: machineHistory.machineMaintenances,
                        archivedDate: machineHistory.archivedDate
                    },
                    cells: [
                        {
                            name: "serviceLife",
                            sortVal: machineHistory.serviceLife,
                            cell: <>{this.withUnit(machineHistory.serviceLife, machineHistory.machine.machineType.serviceLifeUnit)}</>,
                        },
                        {
                            name: "machineDefect",
                            sortVal: this.evalMachineDefectVals(machineHistory)[0].defect,
                            cell: this.evalMachineDefects(machineHistory),
                        },
                        {
                            name: "machineMaintenance",
                            sortVal: machineHistory.machineMaintenances && machineHistory.machineMaintenances![0] ? machineHistory.machineMaintenances![0].maintenance.name : '',
                            cell: this.evalMachineMaintenance(machineHistory),
                        },
                    ]
                })
            })
        }
        return data;
    }

    setColumns(): TableColumn[] {
        // TODO maybe dynamic??
        const columns: TableColumn[] = [
            {
                header: 'Id',
                accessor: 'id',
            },
            {
                header: 'Machine',
                accessor: 'machine',
            },
            {
                header: 'Date',
                accessor: 'date',
            },
            {
                header: 'Service Life',
                accessor: 'serviceLife',
            },
            {
                header: 'Fuel',
                accessor: 'fuelStop',
            },
            {
                header: 'Defect',
                accessor: 'machineDefect',
            },
            {
                header: 'Maintenance',
                accessor: 'machineMaintenance',
            },
        ];
        return this.defectsEnabled() ? columns : columns.filter(c => c.accessor !== 'machineDefect');
    }

    editMachineHistories(id: number) {
        const mhToEdit = this.props.machineHistories.find(mh => mh.id === id);
        if (mhToEdit) {
            if ((mhToEdit.machineMaintenances && mhToEdit.machineMaintenances.length > 0) ||
                (mhToEdit.fixedMachineDefects && mhToEdit.fixedMachineDefects.length > 0)) {
                this.props.moveToEditWorkshop(id);
            } else {
                this.props.moveToEditDailyReport(id);
            }

        }
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Machine History" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Machine History" />
                <div className="c-card">
                    <Select
                        value={this.selectedOption()}
                        onChange={selectableOption => this.setState({ selectedOption: selectableOption })}
                        options={this.getOptions()}
                        isClearable
                        placeholder={Translate.text(this.props.translations, "Machine")}
                        className={"c-form__select"}
                        classNamePrefix="c-select"
                        // render the menu outside the card, which would clip it
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 10 }) }}
                    />
                </div>
                <div className="c-card">
                    <Table
                        editable={true}
                        editAction={this.editMachineHistories.bind(this)}
                        addAction={this.props.moveToAddDailyReport}
                        deleteAction={this.props.deleteMachineHistories}
                        columns={this.setColumns()}
                        defaultSort={{ columnId: 'serviceLife', sortType: sortType.desc }}
                        tableData={this.setMachineData()}
                        translations={this.props.translations}
                        loginUser={this.props.loginUser}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineHistories: state.machineHistories,
        fuelTypes: state.fuelTypes,
        machineTypes: state.machineTypes,
        currentSubscription: state.currentSubscription,
        translations: state.translations,
        router: state.router
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        fetchFuelTypes: bindActionCreators(FuelTypeActions.fetchFuelTypes, dispatch),
        deleteMachineHistories: bindActionCreators(MachineHistoryActions.deleteMachineHistory, dispatch),
        moveToEditDailyReport: bindActionCreators(MachineHistoryActions.moveToEditDailyReport, dispatch),
        moveToEditWorkshop: bindActionCreators(MachineHistoryActions.moveToEditWorkshop, dispatch),
        moveToAddDailyReport: bindActionCreators(MachineHistoryActions.moveToAddDailyReport, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MachineHistories);